import { useEffect, useState } from "react";
import api from "../axiosConfig";

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(value) {
  if (!value) return "No activity";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function buildDraft(item) {
  return {
    name: item.user.name || "",
    email: item.user.email || "",
    nickname: item.user.nickname || "",
    password: "",
  };
}

function approvalLabel(item) {
  if (item.user.isAdmin) return "Admin";
  if (item.user.approvalStatus === "denied") return "Denied";
  if (item.user.isApproved) return "Approved";
  return "Pending approval";
}

export default function AdminUserUsagePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingUserId, setEditingUserId] = useState(null);
  const [draft, setDraft] = useState({ name: "", email: "", nickname: "", password: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [approvingUserId, setApprovingUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/api/auth/admin/users/usage");
        if (!cancelled) {
          setItems(res.data.items || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || "Failed to load user usage.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const startEditing = (item) => {
    setEditingUserId(item.user.id);
    setDraft(buildDraft(item));
    setError("");
    setSuccessMessage("");
  };

  const stopEditing = () => {
    setEditingUserId(null);
    setDraft({ name: "", email: "", nickname: "", password: "" });
  };

  const updateItem = (user) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        String(item.user.id) === String(user.id)
          ? {
              ...item,
              user: {
                ...item.user,
                ...user,
              },
            }
          : item
      )
    );
  };

  const handleProfileSubmit = async (userId) => {
    setSavingProfile(true);
    setError("");
    setSuccessMessage("");
    try {
      const res = await api.put(`/api/auth/admin/users/${userId}`, {
        name: draft.name,
        email: draft.email,
        nickname: draft.nickname,
      });
      updateItem(res.data.user);
      setSuccessMessage("User profile updated.");
      stopEditing();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update user profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (userId) => {
    setSavingPassword(true);
    setError("");
    setSuccessMessage("");
    try {
      await api.put(`/api/auth/admin/users/${userId}/password`, {
        newPassword: draft.password,
      });
      setSuccessMessage("User password updated.");
      setDraft((currentDraft) => ({ ...currentDraft, password: "" }));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update password.");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDelete = async (userId) => {
    setDeletingUserId(userId);
    setError("");
    setSuccessMessage("");
    try {
      await api.delete(`/api/auth/admin/users/${userId}`);
      setItems((currentItems) => currentItems.filter((item) => String(item.user.id) !== String(userId)));
      if (String(editingUserId) === String(userId)) {
        stopEditing();
      }
      setSuccessMessage("User deleted.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete user.");
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleApprovalChange = async (item, decision) => {
    setApprovingUserId(item.user.id);
    setError("");
    setSuccessMessage("");
    try {
      const res = await api.put(`/api/auth/admin/users/${item.user.id}/approval`, { decision });
      updateItem(res.data.user);
      setSuccessMessage(res.data.message || "User approval status updated.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update approval status.");
    } finally {
      setApprovingUserId(null);
    }
  };

  return (
    <section className="profile-panel admin-usage-panel">
      <span className="eyebrow">Admin</span>
      <div className="section-heading">
        <div>
          <h2>User Data Usage</h2>
        </div>
      </div>

      {error ? <div className="feedback-panel error">{error}</div> : null}
      {successMessage ? <div className="feedback-panel">{successMessage}</div> : null}
      {loading ? <div className="feedback-panel">Loading usage summary...</div> : null}

      {!loading && !items.length ? (
        <div className="feedback-panel">No user usage data available.</div>
      ) : null}

      {!!items.length ? (
        <div className="admin-usage-table-shell">
          <table className="admin-usage-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Titles</th>
                <th>Notes</th>
                <th>Stored text</th>
                <th>Last activity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isEditing = String(editingUserId) === String(item.user.id);
                return (
                  <tr key={item.user.id}>
                    <td>
                      <strong>{item.user.name || "Unnamed user"}</strong>
                      <div className="admin-usage-subtext">{item.user.isAdmin ? "Admin" : "User"}</div>
                    </td>
                    <td>{item.user.email}</td>
                    <td>{item.usage.titleCount}</td>
                    <td>{item.usage.noteCount}</td>
                    <td>{formatBytes(item.usage.noteBytes)}</td>
                    <td>{formatDateTime(item.usage.lastActivityAt)}</td>
                    <td>
                      <span className={`admin-approval-badge ${item.user.approvalStatus === "denied" ? "is-denied" : item.user.isApproved ? "is-approved" : "is-pending"}`}>
                        {approvalLabel(item)}
                      </span>
                    </td>
                    <td>
                      <div className="admin-usage-actions">
                        {!item.user.isAdmin ? (
                          <>
                            {item.user.approvalStatus !== "approved" ? (
                              <button
                                type="button"
                                className="action-button-secondary"
                                onClick={() => handleApprovalChange(item, "approved")}
                                disabled={approvingUserId === item.user.id}
                              >
                                {approvingUserId === item.user.id ? "Saving..." : "Approve user"}
                              </button>
                            ) : null}
                            {item.user.approvalStatus !== "denied" ? (
                              <button
                                type="button"
                                className="action-button-secondary"
                                onClick={() => handleApprovalChange(item, "denied")}
                                disabled={approvingUserId === item.user.id}
                              >
                                {approvingUserId === item.user.id ? "Saving..." : "Deny user"}
                              </button>
                            ) : null}
                            {item.user.approvalStatus !== "pending" ? (
                              <button
                                type="button"
                                className="action-button-secondary"
                                onClick={() => handleApprovalChange(item, "pending")}
                                disabled={approvingUserId === item.user.id}
                              >
                                {approvingUserId === item.user.id ? "Saving..." : "Mark pending"}
                              </button>
                            ) : null}
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="action-button-secondary"
                          onClick={() => (isEditing ? stopEditing() : startEditing(item))}
                        >
                          {isEditing ? "Close" : "Edit"}
                        </button>
                        <button
                          type="button"
                          className="action-link danger"
                          onClick={() => handleDelete(item.user.id)}
                          disabled={deletingUserId === item.user.id}
                        >
                          {deletingUserId === item.user.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>

                      {isEditing ? (
                        <div className="admin-usage-editor">
                          <label className="field-row">
                            <span>Name</span>
                            <input
                              type="text"
                              value={draft.name}
                              onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                            />
                          </label>
                          <label className="field-row">
                            <span>Email</span>
                            <input
                              type="email"
                              value={draft.email}
                              onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
                            />
                          </label>
                          <label className="field-row">
                            <span>Nickname</span>
                            <input
                              type="text"
                              value={draft.nickname}
                              onChange={(event) => setDraft((current) => ({ ...current, nickname: event.target.value }))}
                            />
                          </label>
                          <div className="form-actions admin-usage-editor-actions">
                            <button
                              type="button"
                              className="action-button-secondary"
                              onClick={() => handleProfileSubmit(item.user.id)}
                              disabled={savingProfile}
                            >
                              {savingProfile ? "Saving..." : "Save profile"}
                            </button>
                          </div>

                          <label className="field-row">
                            <span>Reset password</span>
                            <input
                              type="password"
                              value={draft.password}
                              placeholder="Enter new password"
                              onChange={(event) => setDraft((current) => ({ ...current, password: event.target.value }))}
                            />
                          </label>
                          <div className="form-actions admin-usage-editor-actions">
                            <button
                              type="button"
                              className="action-button-secondary"
                              onClick={() => handlePasswordSubmit(item.user.id)}
                              disabled={savingPassword || !draft.password}
                            >
                              {savingPassword ? "Updating..." : "Update password"}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
